import { api } from '../api/client';
import { device } from '../platform';
import { API_AUTH_REFRESH_URL, API_DEVICE_SESSIONS_URL } from '../utils';

/**
 * Device sessions (KPR-1674; keepr-api docs/SCHEMA.md § Device sessions).
 *
 * The phone app signs in on a device session: a 1-hour token plus a refresh
 * credential that rotates (the api client renews it, src/api/client.js). A
 * browser signs in with the web's 4-week token and starts none, but Account >
 * Devices lists the person's device sessions on every platform, so a lost
 * phone can be signed out from a laptop.
 */

/**
 * The `device` a sign-in sends, or nothing: `{ device: { platform, name } }`
 * in the app (the platform module says so), `{}` in a browser. Spread it into
 * the body of both sign-in legs: the API starts the device session on the leg
 * that mints the session, and reads only that leg's own `device`.
 */
export function signInDevice() {
	let asked = null;
	try { asked = device?.forSignIn?.() || null; } catch { asked = null; }
	return asked && asked.platform ? { device: asked } : {};
}

/** The caller's live device sessions, most recently used first; `current` marks this one. */
export async function listDeviceSessions() {
	const { data } = await api.get(API_DEVICE_SESSIONS_URL);
	return Array.isArray(data?.deviceSessions) ? data.deviceSessions : [];
}

/** Sign one device out (204; idempotent for the caller's own, 404 for anyone else's). */
export async function endDeviceSession(id) {
	await api.delete(`${API_DEVICE_SESSIONS_URL}/${encodeURIComponent(id)}`);
}

function claimsOf(token) {
	try {
		const part = String(token || '').split('.')[1];
		if (!part) return null;
		return JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
	} catch {
		return null;
	}
}

/**
 * Sign out on this device, after the session has been forgotten here
 * (Logout.js): ends its device session on the server, so the list no longer
 * shows it and its refresh credential is dead. Takes the pair the session
 * held (`{ token, refreshToken }`), since the session no longer holds it. A
 * token whose hour has passed is renewed first, used for this call only and
 * never kept. Never throws: the device is signed out either way, and an
 * unreachable server leaves a session nobody holds, which lapses unused.
 */
export async function endSignedOutDevice({ token, refreshToken } = {}) {
	const claims = claimsOf(token);
	const id = claims?.deviceSessionId;
	if (!id || !refreshToken) return false;
	try {
		let bearer = token;
		const expired = !Number.isFinite(claims.exp) || claims.exp * 1000 <= Date.now();
		if (expired) {
			const { data } = await api.post(API_AUTH_REFRESH_URL, { refreshToken });
			bearer = data?.token;
			if (!bearer) return false;
		}
		await api.delete(`${API_DEVICE_SESSIONS_URL}/${encodeURIComponent(id)}`, {
			headers: { Authorization: `Bearer ${bearer}` },
		});
		return true;
	} catch {
		return false;
	}
}
